import { queryOptions, useQuery } from "@tanstack/react-query";

import { supabaseClient } from "@/lib/supabase";
import { isUuid } from "@/services/isUuid";
import type { CarriedAnswerT } from "../utils/markCarriedInputs";

/** The diary's latest answer to each of the form's carry-forward inputs, from entries other than `entryId`. */
export async function selectCarryForwardAnswers(
    diaryId: string,
    formId: string,
    entryId?: string
): Promise<[string, CarriedAnswerT][]> {
    const { data, error } = await supabaseClient.rpc("select_carry_forward_values", {
        p_diary_id: diaryId,
        p_form_id: formId,
        p_exclude_entry_id: entryId,
    });
    if (error) throw error;
    return (data ?? []).flatMap((row): [string, CarriedAnswerT][] => {
        if (row.text_array?.length) return [[row.input_id, row.text_array]];
        if (row.number_value != null) return [[row.input_id, row.number_value]];
        if (row.text_value != null) return [[row.input_id, row.text_value]];
        return [];
    });
}

export const carryForwardAnswersQueryOptions = (diaryId: string | undefined, formId: string, entryId?: string) =>
    queryOptions({
        queryKey: ["carry-forward-answers", { diary_id: diaryId, form_id: formId, entry_id: entryId }],
        queryFn: () => selectCarryForwardAnswers(diaryId!, formId, entryId),
        enabled: !!diaryId && isUuid(diaryId) && isUuid(formId),
    });

export function useSelectCarryForwardAnswers(diaryId: string | undefined, formId: string, entryId?: string) {
    return useQuery(carryForwardAnswersQueryOptions(diaryId, formId, entryId));
}
